import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import './admin-panel.scss';

import { ROUTE_PATH } from '../../../route/utils';
import Navigation from '../../navigation/Navigation';
import { AdminNav } from '../../common/admin-nav/AdminNav';
import { MenuCategoryTable } from '../menu/table/MenuCategoryTable';
import { MenuItemTable } from '../menu/table/MenuItemTable';
import { MenuTable } from '../menu/table/MenuTable';
import { DeliveryConfigPanel } from './delivery-config/DeliveryConfigPanel';
import { DeliveryZonesPanel } from './delivery-zones/DeliveryZonesPanel';
import { CustomFieldsTable } from './meal-builder/CustomFieldsTable';
import { CustomFieldItemsTable } from './meal-builder/CustomFieldItemsTable';
import { ConsultantsPanel } from './sales/ConsultantsPanel';
import { CouponsPanel } from './sales/CouponsPanel';
import { PromotionsPanel } from './sales/PromotionsPanel';
import { SalesReportsPanel } from './sales/SalesReportsPanel';
import { SalesSettingsPanel } from './sales/SalesSettingsPanel';
import { PaymentsPanel } from './sales/PaymentsPanel';

export const NAV_SECTIONS = [
  {
    key: 'menu',
    label: 'Menu',
    items: [
      { key: 'menus', label: 'Menus' },
      { key: 'menu-items', label: 'Menu Items' },
      { key: 'menu-categories', label: 'Menu Categories' }
    ]
  },
  {
    key: 'delivery',
    label: 'Delivery',
    items: [
      { key: 'delivery-config', label: 'Config' },
      { key: 'delivery-zones', label: 'Delivery Zones' }
    ]
  },
  {
    key: 'meal-builder',
    label: 'Meal Builder',
    items: [
      { key: 'custom-fields', label: 'Custom Fields' },
      { key: 'custom-field-items', label: 'Custom Field Items' }
    ]
  },
  {
    key: 'sales',
    label: 'Sales',
    items: [
      { key: 'sales-orders', label: 'Orders', route: ROUTE_PATH.ORDER },
      { key: 'consultants', label: 'Consultants' },
      { key: 'coupons', label: 'Coupon Codes' },
      { key: 'promotions', label: 'Promotions' },
      { key: 'sales-payments', label: 'Payments' },
      { key: 'sales-reports', label: 'Reports' },
      { key: 'sales-settings', label: 'Settings' }
    ]
  }
];

const TITLE_MAP = {
  menus: 'Menus',
  'menu-items': 'Menu Items',
  'menu-categories': 'Menu Categories',
  'delivery-config': 'Delivery Config',
  'delivery-zones': 'Delivery Zones',
  'custom-fields': 'Custom Fields',
  'custom-field-items': 'Custom Field Items',
  consultants: 'Consultants',
  coupons: 'Coupon Codes',
  promotions: 'Promotions',
  'sales-payments': 'Payments',
  'sales-reports': 'Sales Reports',
  'sales-settings': 'Sales Settings'
};

const PANEL_MAP = {
  menus: <MenuTable />,
  'menu-items': <MenuItemTable />,
  'menu-categories': <MenuCategoryTable />,
  'delivery-config': <DeliveryConfigPanel />,
  'delivery-zones': <DeliveryZonesPanel />,
  'custom-fields': <CustomFieldsTable />,
  'custom-field-items': <CustomFieldItemsTable />,
  consultants: <ConsultantsPanel />,
  coupons: <CouponsPanel />,
  promotions: <PromotionsPanel />,
  'sales-payments': <PaymentsPanel />,
  'sales-reports': <SalesReportsPanel />,
  'sales-settings': <SalesSettingsPanel />
};

const SCROLLABLE_PANELS = new Set(['delivery-config', 'sales-settings']);

export const AdminPanel = () => {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(location.state?.activeKey || 'menus');
  const navigate = useNavigate();

  const handleNavChange = (key) => {
    const item = NAV_SECTIONS.flatMap((section) => section.items).find((i) => i.key === key);
    if (item?.route) {
      navigate(item.route);
    } else {
      setActiveKey(key);
    }
  };

  return (
    <Navigation>
      <div className="admin-panel">
        <AdminNav
          sections={NAV_SECTIONS}
          activeSection={activeKey}
          onChange={handleNavChange}
        />
        <div className="admin-panel__content">
          <div className="admin-panel__title">{TITLE_MAP[activeKey]}</div>
          <div className={`admin-panel__panel${SCROLLABLE_PANELS.has(activeKey) ? ' admin-panel__panel--scrollable' : ''}`}>
            {PANEL_MAP[activeKey]}
          </div>
        </div>
      </div>
    </Navigation>
  );
};
